(function () {
  if (window.StrideZoomDialog) return;

  class ZoomDialog extends HTMLElement {
  constructor() {
    super();
    this.dialog = null;
    this.mediaSlider = null;
    this.mediaElements = [];
    this.currentIndex = 0;
    this.highResImagesLoaded = new Set();
    this.swiper = null;
    this.swiperInitTimer = null;
    this.wheelLock = false;
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.mediaSlider = this.querySelector('.zoom-dialog__slider');
    this.mediaList = this.querySelector('.zoom-dialog__media-list');
    this.mediaElements = Array.from(this.querySelectorAll('.zoom-dialog__media-item'));
    this.closeButton = this.querySelector('.zoom-dialog__close');
    this.scrollHintDots = Array.from(this.querySelectorAll('.zoom-dialog__dot'));

    if (!this.dialog) return;

    this.closeButton?.addEventListener('click', () => this.close());
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('keydown', (e) => this.handleKeyDown(e));
  
    if (this.mediaSlider) {
      this.mediaSlider.addEventListener('wheel', (e) => this.handleWheel(e), { passive: false });
    }

    this.scrollHintDots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.navigateTo(index));
    });

    this.ensureSwiper();
  }

  disconnectedCallback() {
    if (this.swiperInitTimer) {
      clearInterval(this.swiperInitTimer);
      this.swiperInitTimer = null;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  open(index = 0) {
    if (!this.dialog) return;

    this.currentIndex = this.clampIndex(index);
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    this.dialog.focus();

    if (this.swiper) {
      this.swiper.update();
      this.swiper.slideTo(this.currentIndex, 0, false);
    } else {
      const targetMedia = this.mediaElements[this.currentIndex];
      if (targetMedia) {
        this.scrollToMedia(targetMedia, 'auto');
      }
    }

    this.loadHighResImage(this.mediaElements[this.currentIndex]);
    this.updateScrollHint(this.currentIndex);
    this.preloadAdjacentImages(this.currentIndex);
  }

  close() {
    if (!this.dialog) return;

    this.dialog.close();
    document.body.style.overflow = '';

    this.mediaElements.forEach(media => {
      const zoomWrapper = media.querySelector('drag-zoom-wrapper');
      if (zoomWrapper && typeof zoomWrapper.reset === 'function') {
        zoomWrapper.reset();
      }
    });
  }

  handleKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.navigateTo(this.clampIndex(this.currentIndex - 1));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.navigateTo(this.clampIndex(this.currentIndex + 1));
    }
  }

  handleWheel(e) {
    if (!this.mediaSlider) return;
    if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;

    e.preventDefault();
    if (this.swiper) {
      if (this.wheelLock) return;

      this.wheelLock = true;
      if (e.deltaY > 0) {
        this.swiper.slideNext();
      } else {
        this.swiper.slidePrev();
      }
      setTimeout(() => {
        this.wheelLock = false;
      }, 220);
      return;
    }

    this.mediaSlider.scrollLeft += e.deltaY;
  }

  updateScrollHint(activeIndex) {
    if (!this.scrollHintDots || this.scrollHintDots.length === 0) return;
    
    this.scrollHintDots.forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
    });
  }

  navigateTo(index) {
    const safeIndex = this.clampIndex(index);
    this.currentIndex = safeIndex;
    if (this.swiper) {
      this.swiper.slideTo(safeIndex);
    } else {
      const targetMedia = this.mediaElements[safeIndex];
      if (targetMedia) {
        this.scrollToMedia(targetMedia, 'smooth');
      }
    }
    this.updateScrollHint(safeIndex);
  }

  scrollToMedia(mediaElement, behavior) {
    if (!this.mediaSlider || !mediaElement) return;
    this.mediaSlider.scrollTo({
      left: mediaElement.offsetLeft,
      behavior
    });
  }

  clampIndex(index) {
    if (this.mediaElements.length === 0) return 0;
    return Math.min(Math.max(index, 0), this.mediaElements.length - 1);
  }

  initSwiper() {
    if (!this.mediaSlider || this.mediaElements.length <= 1) return;
    if (typeof window.Swiper === 'undefined') return;

    this.swiper = new window.Swiper(this.mediaSlider, {
      slidesPerView: 1,
      speed: 350,
      allowTouchMove: true,
      grabCursor: true,
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      keyboard: { enabled: false },
      on: {
        slideChange: (swiper) => {
          const index = this.clampIndex(swiper.activeIndex);
          this.currentIndex = index;
          this.updateScrollHint(index);
          this.loadHighResImage(this.mediaElements[index]);
          this.preloadAdjacentImages(index);
        }
      }
    });

    if (this.swiperInitTimer) {
      clearInterval(this.swiperInitTimer);
      this.swiperInitTimer = null;
    }
  }

  ensureSwiper() {
    this.initSwiper();
    if (this.swiper || this.swiperInitTimer) return;

    let attempts = 0;
    this.swiperInitTimer = setInterval(() => {
      attempts += 1;
      this.initSwiper();
      if (this.swiper || attempts >= 30) {
        clearInterval(this.swiperInitTimer);
        this.swiperInitTimer = null;
      }
    }, 100);
  }

  loadHighResImage(mediaElement) {
    if (!mediaElement.classList.contains('zoom-dialog__media-item--image')) return;

    const img = mediaElement.querySelector('img');
    if (!img) return;

    const highResUrl = img.dataset.highRes;
    if (!highResUrl || this.highResImagesLoaded.has(highResUrl)) return;

    const highResImg = new Image();
    highResImg.onload = () => {
      img.srcset = highResUrl;
      img.src = highResUrl;
      this.highResImagesLoaded.add(highResUrl);
    };
    highResImg.src = highResUrl;
  }

  preloadAdjacentImages(index) {
    const prev = this.mediaElements[index - 1];
    const next = this.mediaElements[index + 1];

    [prev, next].forEach(media => {
      if (media) this.loadHighResImage(media);
    });
  }
  }

  window.StrideZoomDialog = ZoomDialog;

  if (!customElements.get('zoom-dialog')) {
    customElements.define('zoom-dialog', ZoomDialog);
  }
})();